import { useEffect } from 'react';
import Loading from './Loading';
import Progress from './Progress';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { usePortal } from '../../../hooks/useNavConfig';
import { fetchGrowthPlanProgress, fetchOnboardingProgress, getCoac0001Treatment, getCoac0002Treatment } from '../../../reducers/OnboardingProgress';
import { captureException } from '../../../utils/sentryAPI';
import { useIsUngated } from '../../../hooks/useIsUngated';
import { REMOVE_GROWTH_PLAN_GATE } from '../../../constants/gates';
import usePLPOData from '../../../hooks/usePLPOData';
import { withErrorBoundary } from '../../ErrorBoundary';
import memoWithDisplayName from '../../../utils/memoWithDisplayName';
import { jsx as _jsx } from "react/jsx-runtime";
const _OnboardingProgress = ({
  isEnrolledInOnboarding
}) => {
  const dispatch = useAppDispatch();
  const {
    isFetchingOnboardingProgress,
    hasError,
    progress,
    selectedView,
    groupKey
  } = useAppSelector(state => state.onboardingProgress);
  const coac0001VariantTreatment = useAppSelector(getCoac0001Treatment);
  const coac0002VariantTreatment = useAppSelector(getCoac0002Treatment);
  const isUngatedToGrowthPlanRemoval = useIsUngated(REMOVE_GROWTH_PLAN_GATE);
  const {
    portalId
  } = usePortal();
  const {
    isEligible: isPlpoEligible,
    groupKey: plpoGroupKey,
    progress: plpoProgress,
    isFetching: isFetchingPLPO,
    hasError: hasPlpoError
  } = usePLPOData();
  const isInCoac0001Variant = coac0001VariantTreatment === 'variant_a' || coac0001VariantTreatment === 'variant_b';
  const isInCoac0002Variant = coac0002VariantTreatment === 'variant_a' || coac0002VariantTreatment === 'variant_b';
  const isEligbleForGrowthPlan = coac0001VariantTreatment && isInCoac0001Variant || coac0002VariantTreatment && isInCoac0002Variant;
  const shouldSeeGrowthPlan = !isUngatedToGrowthPlanRemoval && !!isEligbleForGrowthPlan;
  useEffect(() => {
    // don't fetch onboarding progress from setup guide or growth plans if PLPO is eligible or fetching
    if (isPlpoEligible || isFetchingPLPO) {
      return;
    }
    if (portalId && !hasError && !isFetchingOnboardingProgress && !groupKey) {
      if (shouldSeeGrowthPlan) {
        dispatch(fetchGrowthPlanProgress(portalId)).catch(captureException);
      } else {
        dispatch(fetchOnboardingProgress(portalId)).catch(captureException);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSeeGrowthPlan, isPlpoEligible, isFetchingPLPO]);
  if (!isEnrolledInOnboarding && !isPlpoEligible) {
    return null;
  }
  if (isFetchingOnboardingProgress || isFetchingPLPO) {
    return /*#__PURE__*/_jsx(Loading, {});
  }
  if (hasError || hasPlpoError) {
    return null;
  }
  if (isPlpoEligible && !plpoGroupKey) {
    return null;
  }
  return /*#__PURE__*/_jsx(Progress, {
    progress: progress,
    selectedView: selectedView,
    groupKey: groupKey,
    redirectToGrowthPlan: shouldSeeGrowthPlan,
    isPlpoEligible: isPlpoEligible,
    plpoGroupKey: plpoGroupKey,
    plpoProgress: plpoProgress
  });
};
export const OnboardingProgress = withErrorBoundary(memoWithDisplayName('OnboardingProgress', _OnboardingProgress), {
  boundaryName: 'OnboardingProgress'
});